import { getPlanOriginalPrice } from '../../utils/plan-original-price'
import { formatCurrency } from '../../utils/currency'

// Fim da comunicação da Black November na tela: 30/11 00:00 em Brasília (UTC-3).
// O banner diz "de 03 a 29/11" de propósito. O preço da campanha vale até 07/12 (vigência
// da tabela no billing), mas de 30/11 em diante entra outra comunicação (Cyber Week, ainda
// sem design). Até ela existir, banner, selo e quadro somem e o card fica só com o preço
// e o riscado, que vêm do banco.
export const CAMPAIGN_BANNER_ENDS_AT = Date.UTC(2026, 10, 30, 3, 0, 0)

const NO_CALLS_FEATURE_NAME = 'nao-realiza-ligacoes'
const CALL_MINUTES_FEATURE_PREFIX = 'minutos-ligacoes-'
const IA4COM_FEATURE_NAME = 'ia4com'
const IA4COM_TWIN_SUFFIX = '-ia4com'
// No billing só o Gerencial tem a feature `acesso-basico`, mas todo plano dá acesso à
// plataforma: como linha comum, ela marcaria Negociação e Ilimitado como sem acesso.
const PLATFORM_ACCESS_FEATURE_NAME = 'acesso-basico'
const UNBOUNDED_MINUTES = Infinity

const SECOND_MS = 1000
const MINUTE_SECONDS = 60
const HOUR_SECONDS = 60 * MINUTE_SECONDS
const DAY_SECONDS = 24 * HOUR_SECONDS

/**
 * A conta vê a campanha quando o catálogo de contratação dela tem oferta de tabela
 * restrita (o billing só devolve essas ofertas a quem tem direito) e o prazo da
 * comunicação ainda não acabou.
 */
export function isCampaignBannerActive (plans, now) {
  if (now >= CAMPAIGN_BANNER_ENDS_AT) return false
  return Array.isArray(plans) && plans.some(plan => plan.isCampaign === true)
}

function pad (value) {
  return value < 10 ? '0' + value : String(value)
}

// Contador "Termina em" do banner, em dias, horas, minutos e segundos.
export function campaignCountdown (now) {
  const remainingSeconds = Math.max(0, Math.floor((CAMPAIGN_BANNER_ENDS_AT - now) / SECOND_MS))
  return {
    days: pad(Math.floor(remainingSeconds / DAY_SECONDS)),
    hours: pad(Math.floor((remainingSeconds % DAY_SECONDS) / HOUR_SECONDS)),
    minutes: pad(Math.floor((remainingSeconds % HOUR_SECONDS) / MINUTE_SECONDS)),
    seconds: pad(remainingSeconds % MINUTE_SECONDS)
  }
}

/**
 * Desconto do preço atual sobre o riscado, em % inteiro, ou `null` sem riscado.
 * Sai da oferta, e não de texto fixo: com IA4COM a Black dá 69% (104,95 de 339,80) e
 * sem IA4COM dá 50% (104,95 de 209,90).
 */
export function discountPercent (plan) {
  const originalPrice = getPlanOriginalPrice(plan)
  if (!originalPrice) return null
  return Math.round((1 - plan.monthlyFee / originalPrice) * 100)
}

/**
 * Desconto da Black November para a frase "… com X% off nos 2 primeiros meses", ou
 * `null` quando o plano não leva a frase. Mesma regra do quadro preto dos cards: oferta
 * da campanha com `highlight`, com riscado, e só até o fim da comunicação (29/11).
 */
export function campaignDiscount (plan, now) {
  if (now >= CAMPAIGN_BANNER_ENDS_AT) return null
  if (!plan || plan.isCampaign !== true || plan.highlight !== true) return null
  return discountPercent(plan)
}

function minutesOrder (plan) {
  return plan.minutes === null || plan.minutes === undefined ? UNBOUNDED_MINUTES : plan.minutes
}

/**
 * Cards da vez: só os planos com IA4COM ou só os sem, conforme a chave, na ordem do
 * Figma (Gerencial, Negociação, Ilimitado). A ordem é pelos minutos, não pelo preço:
 * na Black o Ilimitado fica mais barato que a Negociação.
 */
export function showcasePlans (plans, withIa4com) {
  if (!Array.isArray(plans)) return []
  return plans
    .filter(plan => (plan.hasIa4com === true) === withIa4com)
    .sort((planA, planB) => {
      const minutesDifference = minutesOrder(planA) - minutesOrder(planB)
      if (minutesDifference !== 0) return minutesDifference
      return planA.monthlyFee - planB.monthlyFee
    })
}

/**
 * Benefícios do card com os textos do billing, como estão (decisão do revamp, a
 * revisitar). O IA4COM fica de fora: o card mostra ele à parte, com o link do modal.
 */
export function cardBenefits (plan) {
  const features = Array.isArray(plan && plan.features) ? plan.features : []
  return features
    .filter(feature => feature && feature.name !== IA4COM_FEATURE_NAME)
    .map((feature, index) => ({
      key: feature.name || String(index),
      label: feature.title || feature.name,
      included: feature.name !== NO_CALLS_FEATURE_NAME
    }))
    .filter(benefit => !!benefit.label)
}

function isCallFeature (feature) {
  return feature.name === NO_CALLS_FEATURE_NAME || feature.name.indexOf(CALL_MINUTES_FEATURE_PREFIX) === 0
}

function callsLabel (plan) {
  if (plan.minutes === null || plan.minutes === undefined) return 'Ilimitadas'
  if (plan.minutes === 0) return 'Não faz'
  return `Até ${plan.minutes} min`
}

// Quanto o IA4COM soma ao plano: diferença para o gêmeo `X-ia4com`. Na Black o Ilimitado
// + IA4COM custa o mesmo que o Ilimitado e a linha diz "+ R$ 0,00": continua sendo um
// adicional (é preciso escolher o gêmeo), só que sem custo. "Incluso" faria parecer que o
// Ilimitado comum já traz o IA4COM.
function ia4comLabel (plan, plans) {
  const twin = plans.find(other => other.code === plan.code + IA4COM_TWIN_SUFFIX)
  if (!twin) return '—'
  const difference = Math.round((twin.monthlyFee - plan.monthlyFee) * 100) / 100
  return '+ ' + formatCurrency(Math.max(difference, 0))
}

/**
 * Tabela "Comparar todos os recursos" (Figma da TEL-2465): uma coluna por plano base, sem
 * os gêmeos IA4COM, que entram como a última linha ("+ R$ X"). Os recursos saem das
 * features do billing, na ordem em que aparecem subindo de plano (do Gerencial ao
 * Ilimitado), que é a ordem do Figma. Valor de célula: texto, ou
 * `true`/`false` para incluso/não incluso.
 */
export function comparisonTable (plans) {
  const columns = showcasePlans(plans, false)
  if (columns.length === 0) return { columns: [], rows: [] }

  const rows = [
    { label: 'Preço por usuário/mês (mensal)', values: columns.map(plan => plan.monthlyFee === 0 ? 'Grátis' : formatCurrency(plan.monthlyFee)) },
    { label: 'Ligações', values: columns.map(callsLabel) },
    // Tratado no portal (decisão do usuário, 26/10): o texto e a marcação do Figma, em
    // todos os planos, no lugar da feature do billing.
    { label: 'Acesso à plataforma', values: columns.map(() => true) }
  ]

  const featureTitles = []
  columns.forEach(plan => {
    (plan.features || []).forEach(feature => {
      if (!feature || !feature.name || isCallFeature(feature)) return
      if (feature.name === IA4COM_FEATURE_NAME || feature.name === PLATFORM_ACCESS_FEATURE_NAME) return
      const title = feature.title || feature.name
      if (featureTitles.indexOf(title) === -1) featureTitles.push(title)
    })
  })
  featureTitles.forEach(title => {
    rows.push({
      label: title,
      values: columns.map(plan => (plan.features || []).some(feature => feature && (feature.title || feature.name) === title))
    })
  })

  if (plans.some(plan => plan.hasIa4com === true)) {
    rows.push({ label: 'IA4COM', values: columns.map(plan => ia4comLabel(plan, plans)) })
  }

  return { columns, rows }
}



// WEBPACK FOOTER //
// ./src/components/Subscription/plan-showcase.js